import { cn } from "@/utils";
import {
  Anchor as MantineAnchor,
  Badge as MantineBadge,
  Button as MantineButton,
  Card as MantineCard,
  MenuItem as MantineMenuItem,
  NavLink as MantineNavLink,
  Table as MantineTable,
  type AnchorProps,
  type BadgeProps,
  type ButtonProps,
  type CardProps,
  type MenuItemProps,
  type NavLinkProps,
  type PolymorphicComponentProps,
  type TableTrProps,
} from "@mantine/core";
import { createLink, type CreateLinkProps, type LinkComponent } from "@tanstack/react-router";
import type { ReactNode } from "react";

const TableTr = (props: PolymorphicComponentProps<"tr", TableTrProps>) => <MantineTable.Tr {...props} />;
const Badge = (props: PolymorphicComponentProps<"div", BadgeProps>) => <MantineBadge {...props} />;
const Button = (props: PolymorphicComponentProps<"button", ButtonProps>) => <MantineButton {...props} />;
const PrimitiveNavLink = (props: PolymorphicComponentProps<"a", NavLinkProps>) => <MantineNavLink {...props} />;
const Anchor = (props: PolymorphicComponentProps<"a", AnchorProps>) => <MantineAnchor {...props} />;
const MenuItem = (props: PolymorphicComponentProps<"button", MenuItemProps>) => <MantineMenuItem {...props} />;
const Card = (props: PolymorphicComponentProps<"div", CardProps>) => <MantineCard {...props} />;

function createOptionalLink<TProps>(
  Comp: (props: TProps) => ReactNode,
): LinkComponent<(props: TProps & { enabled?: boolean }) => ReactNode> {
  const LinkComp = createLink(Comp) as any;
  return (({
    enabled = true,
    className,
    ...rest
  }: PolymorphicComponentProps<TProps, CreateLinkProps> & {
    className?: string;
    enabled?: boolean;
  }) => {
    if (!enabled)
      return Comp({
        ...rest,
        className: cn("cursor-default!", className),
      } as TProps);
    return <LinkComp {...rest} className={className} />;
  }) as LinkComponent<(props: TProps & { enabled?: boolean }) => ReactNode>;
}

export const TableTrLink = createOptionalLink(TableTr);
export const BadgeLink = createOptionalLink(Badge);
export const ButtonLink = createOptionalLink(Button);
export const NavLink = createOptionalLink(PrimitiveNavLink);
export const AnchorLink = createOptionalLink(Anchor);
export const MenuItemLink = createOptionalLink(MenuItem);
export const CardLink = createOptionalLink(Card);
